import type { UserProfile } from '@/lib/auth/client';
import type { AnalyticsClient } from './client';

/**
 * Stable distinct_id used across the predictions product. We deliberately
 * use the internal Postgres user id (the api uses the same), not the
 * Firebase uid which can rotate per provider. Choosing this once at
 * launch and never changing it is critical — switching distinct_id later
 * poisons historical funnels.
 */
export function distinctIdFor(internalUserId: number): string {
  return `predictions:${internalUserId}`;
}

const state: { currentDistinctId: string | null } = { currentDistinctId: null };

/**
 * Profile fields that map onto Mixpanel's reserved (`$`-prefixed) profile
 * properties. Mixpanel only renders a profile's name/email/avatar when the
 * reserved names are used.
 *
 * This mapping used to happen server-side inside Segment's Mixpanel
 * cloud-mode destination. Now that Mixpanel is a first-party device-mode
 * destination, nothing remaps for us — so we do it here. Dropping this would
 * silently leave every Mixpanel profile blank.
 *
 * @see https://docs.mixpanel.com/docs/data-structure/user-profiles#reserved-user-properties
 */
const RESERVED_TRAIT_NAMES: Readonly<Record<string, string>> = {
  email: '$email',
  firstName: '$first_name',
  lastName: '$last_name',
  displayName: '$name',
  phone: '$phone',
  profileImage: '$avatar',
  createdAt: '$created',
};

/**
 * `userId` and `firebaseUid` are excluded so identity bookkeeping stays
 * separate from profile traits.
 */
const EXCLUDED_KEYS = new Set(['userId', 'firebaseUid']);

/**
 * Translate the auth UserProfile into Mixpanel profile traits. Known fields are
 * renamed to their reserved `$` equivalents; every remaining field passes
 * through as a custom trait. null/undefined values are dropped so we never
 * overwrite a real value with an empty one.
 */
function toTraits(user: UserProfile): Record<string, unknown> {
  const out: Record<string, unknown> = {};

  for (const [k, v] of Object.entries(user)) {
    if (EXCLUDED_KEYS.has(k)) continue;
    if (v === undefined || v === null) continue;
    out[RESERVED_TRAIT_NAMES[k] ?? k] = v;
  }

  return out;
}

/**
 * Drive identify/reset off auth state transitions. Idempotent: calling
 * this with the same user repeatedly is cheap and only fires the SDK
 * the first time. Designed to be called from a React effect that
 * watches the auth profile.
 *
 * Transitions handled:
 *   anon → user        identify + set traits
 *   userA → userB      reset + identify(B)
 *   user → null        reset
 *   null → null        no-op
 */
export function reconcileIdentity(client: AnalyticsClient, user: UserProfile | null): void {
  const nextDistinctId = user ? distinctIdFor(user.userId) : null;
  if (nextDistinctId === state.currentDistinctId) return;

  if (state.currentDistinctId && nextDistinctId !== state.currentDistinctId) {
    client.reset();
    state.currentDistinctId = null;
  }

  if (nextDistinctId && user) {
    client.identify(nextDistinctId);
    client.peopleSet(toTraits(user));
    state.currentDistinctId = nextDistinctId;
  }
}

/** Test-only escape hatch. Internal to the analytics surface. */
export function __resetIdentityStateForTests(): void {
  state.currentDistinctId = null;
}
